import React from "react";
import logo from "../assets/images/CWA_logo.png";
import paidStamp from "../assets/images/stamp.png";
import { format } from "date-fns";

const ReceiptPreview = ({ member, forDownload = false }) => {
  // Define receipt container classes based on whether it's for viewing or downloading
  const containerClasses = forDownload
    ? "relative p-6 bg-white w-full text-black leading-snug text-sm"
    : "relative p-6 bg-white w-full max-w-[794px] mx-auto text-black leading-snug text-sm";

  // Set minimum height equivalent to A4 paper
  const containerStyle = {
    minHeight: "1123px", // A4 height in pixels at 96dpi
  };

  return (
    <div
      id={forDownload ? "receipt-download" : "receipt"}
      className={containerClasses}
      style={containerStyle}
    >
      {/* Watermark - only show on larger screens when not for download */}
      {!forDownload && (
        <div
          className="hidden sm:flex absolute inset-0 items-start justify-center pointer-events-none z-0 opacity-10"
          style={{ paddingTop: "17rem" }}
        >
          <img
            src={logo}
            alt="Watermark"
            className="w-24 md:w-40 lg:w-56"
          />
        </div>
      )}

      {/* Watermark for download version - always show */}
      {forDownload && (
        <div
          className="absolute inset-0 flex items-start justify-center pointer-events-none z-0 opacity-10"
          style={{ paddingTop: "17rem" }}
        >
          <img
            src={logo}
            alt="Watermark"
            className="w-40"
          />
        </div>
      )}

      <div className="relative z-10 space-y-4">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4 space-y-3 md:space-y-0">
          <img src={logo} alt="Temple Logo" className={forDownload ? "w-36" : "w-32 sm:w-36"} />
          <div className="text-sm text-right space-y-1">
            <p className="font-bold text-base sm:text-lg">TKS Trust</p>
            <p className="text-xs">Project Office, Chennai</p>
            <p className="text-xs">PAN: AAFTT1126R</p>
            <p className="text-xs">Unique Registration Number: AAFTT1126RF20251</p>
            <p className="text-xs italic">(Provisionally approved u/s 80G(5)(vi))</p>
            <p className="text-xs">Phone: +91 98418 18740</p>
            <p className="text-xs">Email: finance@tkstrust.com</p>
            <p className="text-xs">Website: www.tkstrust.org</p>
          </div>
        </div>

        {/* Invoice Heading */}
        <div className="bg-yellow-400 text-black font-semibold text-center text-sm-plus border border-gray-500 py-2">
          INVOICE DETAILS
        </div>

        {/* Invoice Details & Stamp */}
        <div className={forDownload ? "flex flex-row justify-between mt-4 gap-4" : "flex flex-col md:flex-row justify-between mt-4 gap-4"}>
          {/* Invoice Info */}
          <div className="text-left flex-1 space-y-2">
            <h2 className="font-bold mb-2">Invoice to</h2>
            <div className="space-y-1 text-sm">
              <div className="flex">
                <span className="w-32 font-semibold">Name</span>
                <span className="px-1">:</span>
                <span>{member.First_Name} {member.Last_Name}</span>
              </div>
              <div className="flex">
                <span className="w-32 font-semibold">Address</span>
                <span className="px-1">:</span>
                <span>{member.City}</span>
              </div>
              <div className="flex">
                <span className="w-32 font-semibold">Date</span>
                <span className="px-1">:</span>
                <span>{format(new Date(member.Payment_Date), 'dd MMMM yyyy')}</span>
              </div>
              <div className="flex">
                <span className="w-32 font-semibold">Payment Method</span>
                <span className="px-1">:</span>
                <span>{member.Payment_Method}</span>
              </div>
              <div className="flex">
                <span className="w-32 font-semibold">Transaction ID</span>
                <span className="px-1">:</span>
                <span>{member.Payment_Id}</span>
              </div>
            </div>
          </div>

          {/* Paid Stamp */}
          <div className={forDownload ? "self-start mr-24" : "self-center md:self-start md:mr-24"}>
            <img src={paidStamp} alt="Paid Stamp" className={forDownload ? "w-20 h-20" : "w-20 h-17 md:w-20 md:h-20"} />
          </div>
        </div>

        <hr className="my-4" />

        {/* Description Heading */}
        <div className="bg-yellow-400 text-black font-semibold text-center text-sm-plus border border-gray-500 py-2">
          DESCRIPTION
        </div>

        {/* Product Table */}
        <div className="overflow-x-auto">
          <table className="w-full border border-gray-300 text-left text-sm">
            <thead className="bg-gray-100">
              <tr>
                <th className="border border-gray-300 px-2 py-1 font-semibold">Service</th>
                <th className="border border-gray-300 px-2 py-1 font-semibold">Validity</th>
                <th className="border border-gray-300 px-2 py-1 font-semibold">Total</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="border border-gray-300 px-2 py-1">Membership</td>
                <td className="border border-gray-300 px-2 py-1">Lifetime</td>
                <td className="border border-gray-300 px-2 py-1">₹ 2000</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Total Row */}
        <div className="flex justify-end mt-4 bg-yellow-400 p-2 font-semibold text-sm-plus">
          <span>Total: ₹ 2000</span>
        </div>

        {/* Summary */}
        <div className="text-left mt-4 space-y-1 pr-2 text-sm">
          <p>Subtotal: ₹ 2000</p>
          <p>Tax (if applicable): ₹ 0</p>
          <p className="font-bold text-base">
            Total Amount : ₹ 2000/-{" "}
            <span className="font-normal text-black-400 text-sm"> [Rupees Two Thousand Only]</span>
          </p>
        </div>

        {/* 80G Certification */}
        <div className="mt-6 text-xs text-gray-800 leading-relaxed">
          <p className="font-bold mb-2">80G Certification</p>
          <p className="font-light">
            This is to certify that <span className="font-medium">THIRUKADAIYUR SENAITHALAIVAR CHARITABLE TRUST</span> is provisionally approved under Section 80G of the Income Tax Act, 1961 (Approval No. <span className="font-medium">AAFTT1126RF20251</span>, valid from AY 2025-26 to AY 2027-28). Donors are eligible for tax benefits as per applicable provisions.
          </p>
        </div>

        {/* Notes */}
        <p className="mt-6 text-xs text-gray-800 leading-relaxed">
          For assistance, feel free to reach out at{" "}
          <span className="font-semibold">finance@tkstrust.com</span> or call{" "}
          <span className="font-semibold">+91 98418 18740</span>
          <span className="block mt-8 italic font-light text-xs">
            --This is a Computer Generated Payment Receipt and does not need a signature--
          </span>
        </p>
      </div>
    </div>
  );
};

export default ReceiptPreview;